/**
 * tracking.js - JDM Seattle
 * =====================================================================
 * REVISED 2026-07-30 after a live runtime audit.
 *
 * SCOPE: this file only fills GENUINE GAPS. Everything the Google &
 * YouTube app already sends has been removed to avoid double-counting.
 *
 * Already handled elsewhere - DO NOT ADD HERE:
 *   page_view, view_item, add_to_cart, begin_checkout,
 *   add_payment_info, purchase, search, scroll   -> Google & YouTube app
 *   phone_link_click                             -> existing pixel
 *
 * What this file adds:
 *   generate_lead   contact form submitted   (specific; form_submit is
 *                   generic and also fires on search)
 *   generate_lead   chat conversation started (was completely untracked)
 *   sign_up         newsletter subscribed
 *   attribution     GCLID / UTM captured and stamped into the contact
 *                   form so the source reaches the CRM
 *
 * Plus one non-analytics repair: the .buy-now-form handler in app.js
 * adds to cart and then never redirects, leaving the customer with no
 * feedback. Fixed here so app.js (mostly a minified bundle) stays
 * untouched.
 *
 * Events are sent through the gtag already on the page. Google Ads
 * conversions should be created by IMPORTING these key events from
 * GA4, not by firing Ads tags here.
 * =====================================================================
 */
(function () {
  'use strict';

  var CFG = window.JDM_TRACKING || {};
  window.dataLayer = window.dataLayer || [];

  /* Mirrors the standard gtag shim, so this works whether or not the
     Google tag has finished loading - the queue is drained on load. */
  function gtagSend() {
    try { window.dataLayer.push(arguments); } catch (e) { /* no-op */ }
  }

  function sendEvent(name, params) {
    gtagSend('event', name, params || {});
  }

  /* ============================================================
     1. Attribution capture
     ============================================================ */
  var ATTR_KEY = 'jdm_attr';

  function captureAttribution() {
    try {
      var params = new URLSearchParams(window.location.search);
      var keys = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid',
                  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
      var found = {};
      var hasAny = false;

      keys.forEach(function (k) {
        var v = params.get(k);
        if (v) { found[k] = v; hasAny = true; }
      });

      if (hasAny) {
        found.landing_page = window.location.pathname;
        if (document.referrer) found.referrer = document.referrer;
        sessionStorage.setItem(ATTR_KEY, JSON.stringify(found));
      }
    } catch (e) { /* no-op */ }
  }

  function getAttribution() {
    try {
      var raw = sessionStorage.getItem(ATTR_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  function attributionSummary() {
    var a = getAttribution();
    return [
      a.utm_source && ('source=' + a.utm_source),
      a.utm_medium && ('medium=' + a.utm_medium),
      a.utm_campaign && ('campaign=' + a.utm_campaign),
      a.gclid && ('gclid=' + a.gclid),
      a.msclkid && ('msclkid=' + a.msclkid),
      a.landing_page && ('landing=' + a.landing_page)
    ].filter(Boolean).join(' | ');
  }

  /* Stamp the source into a hidden field on the contact form so it
     travels with the enquiry into the CRM / notification email. */
  function stampForms() {
    try {
      var summary = attributionSummary();
      if (!summary) return;

      document.querySelectorAll('form[action*="/contact"]').forEach(function (form) {
        if (form.querySelector('[name="contact[attribution]"]')) return;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'contact[attribution]';
        input.value = summary;
        form.appendChild(input);
      });
    } catch (e) { /* no-op */ }
  }

  /* ============================================================
     2. Lead + signup events
     ------------------------------------------------------------
     Shopify appends ?contact_posted=true after a successful
     {% form 'contact' %} submit and ?customer_posted=true after a
     successful newsletter submit. Real URL changes, so they are a
     durable conversion trigger with no theme edits required.
     ============================================================ */
  function fireFormSuccessEvents() {
    try {
      var params = new URLSearchParams(window.location.search);
      var attr = getAttribution();

      if (params.get('contact_posted') === 'true') {
        sendEvent('generate_lead', {
          lead_type: 'contact_form',
          form_location: window.location.pathname,
          campaign_source: attr.utm_source || '',
          campaign_name: attr.utm_campaign || '',
          gclid: attr.gclid || ''
        });
      }

      if (params.get('customer_posted') === 'true') {
        sendEvent('sign_up', { method: 'newsletter' });
      }
    } catch (e) { /* no-op */ }
  }

  /* ============================================================
     3. LeadConnector (GoHighLevel) chat - previously untracked
     ============================================================ */
  function bindChat() {
    try {
      window.addEventListener('message', function (event) {
        if (!event.origin || event.origin.indexOf('leadconnectorhq.com') === -1) return;

        var data = event.data;
        var type = (data && (data.type || data.event)) || '';
        if (typeof type !== 'string') return;

        if (/submit|lead|contact|conversation_started/i.test(type)) {
          var attr = getAttribution();
          sendEvent('generate_lead', {
            lead_type: 'chat',
            chat_event: type,
            campaign_source: attr.utm_source || '',
            campaign_name: attr.utm_campaign || '',
            gclid: attr.gclid || ''
          });
        } else if (/open/i.test(type)) {
          sendEvent('chat_open', {});
        }
      }, false);
    } catch (e) { /* no-op */ }
  }

  /* ============================================================
     4. Optional phone event - OFF by default
     ------------------------------------------------------------
     An existing pixel already emits phone_link_click. Turn this on
     ONLY if a click test proves some tel: links are uncovered, via
     Settings -> Custom data -> Shop -> Tracking. Uses a distinct
     event name so it can never inflate the existing one.
     ============================================================ */
  function bindPhoneClicks() {
    if (!CFG.emitPhoneEvent) return;

    document.addEventListener('click', function (e) {
      try {
        var link = e.target && e.target.closest && e.target.closest('a[href^="tel:"]');
        if (!link) return;
        sendEvent('contact_phone_click', {
          contact_value: (link.getAttribute('href') || '').replace(/^tel:/, ''),
          page_path: window.location.pathname
        });
      } catch (err) { /* no-op */ }
    }, true);
  }

  /* ============================================================
     5. Repair: .buy-now-form adds to cart but never redirects.
     Capture-phase listener runs before the broken handler in
     app.js and stops it, so app.js needs no edit.
     ============================================================ */
  function fixBuyNowForms() {
    document.addEventListener('submit', function (e) {
      try {
        var form = e.target;
        if (!form || !form.classList || !form.classList.contains('buy-now-form')) return;

        e.preventDefault();
        e.stopPropagation();

        fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
          .catch(function () { /* no-op */ })
          .then(function () { window.location.href = '/checkout'; });
      } catch (err) { /* no-op */ }
    }, true);
  }

  /* ============================================================
     Init
     ============================================================ */
  captureAttribution();
  bindChat();
  bindPhoneClicks();
  fixBuyNowForms();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      fireFormSuccessEvents();
      stampForms();
    });
  } else {
    fireFormSuccessEvents();
    stampForms();
  }
})();
